/**
 * The question set a volunteer is described by, in one place.
 *
 * Two forms ask it: the public registration page, where somebody describes
 * themselves, and the staff "Add a volunteer" panel, where a member of staff
 * describes them — usually with the person on the phone. They used to ask
 * different things. The staff form collected six fields and the profile carried
 * the rest, so a volunteer entered by hand had no age band, no state, no
 * interests and no frequency until somebody went back and filled them in, which
 * is a thing nobody goes back and does.
 *
 * Both forms now build their fields from the constants here, so a question added
 * to one is a question the other asks too. What they are still allowed to differ
 * on is *required*: a visitor filling in their own registration knows their own
 * surname and town; staff taking a name at a session often do not yet.
 */

import {
  AGE_GROUP_LABELS,
  AGE_GROUPS,
  OCCUPATION_LABELS,
  OCCUPATIONS,
  REFERENCE_CATEGORY,
  VOLUNTEER_TYPE,
  VOLUNTEERING_FREQUENCY,
} from '@parinaam-vms/shared';
import type { ReferenceValue } from '@parinaam-vms/shared';
import type { SelectOption } from '../components/common';

/**
 * The three ways someone can say they are volunteering, and the whole list for
 * both forms — the staff panel asks the same question about somebody else.
 *
 * `INDIVIDUAL` first, as the common case; `CORPORATE` reveals the partner list;
 * `STUDENT` records the affiliation and needs nothing more. The organisation is
 * deliberately NOT a box a volunteer can type into — that is how `Infosys`,
 * `infosys` and `Infosys Ltd` pile up. They pick from the list staff maintain,
 * or they are not corporate.
 */
export const VOLUNTEER_TYPE_OPTIONS: SelectOption[] = [
  { value: VOLUNTEER_TYPE.INDIVIDUAL, label: 'As an individual' },
  { value: VOLUNTEER_TYPE.CORPORATE, label: 'Through my organisation (corporate)' },
  { value: VOLUNTEER_TYPE.STUDENT, label: 'As a student' },
];

/**
 * What they do — a fixed set (registration spec). Built from the shared codes so
 * the order and wording are the single source in `OCCUPATION_LABELS`. Choosing
 * `OTHER` reveals the "please specify" box below.
 */
export const OCCUPATION_OPTIONS: SelectOption[] = OCCUPATIONS.map((code) => ({
  value: code,
  label: OCCUPATION_LABELS[code],
}));

/** The age bands (registration spec, 13 Sep 2026) — replaces date of birth. */
export const AGE_GROUP_OPTIONS: SelectOption[] = AGE_GROUPS.map((code) => ({
  value: code,
  label: AGE_GROUP_LABELS[code],
}));

/**
 * How often they want to help — a fixed set, plus an open OTHER answer that
 * reveals a "please specify" box (13 Sep 2026).
 */
export const FREQUENCY_OPTIONS: SelectOption[] = [
  { value: VOLUNTEERING_FREQUENCY.WEEKLY, label: 'Weekly' },
  { value: VOLUNTEERING_FREQUENCY.MONTHLY, label: 'Monthly' },
  { value: VOLUNTEERING_FREQUENCY.QUARTERLY, label: 'Quarterly' },
  { value: VOLUNTEERING_FREQUENCY.OCCASIONALLY, label: 'Occasionally' },
  { value: VOLUNTEERING_FREQUENCY.OTHER, label: 'Other' },
];

/** The staff-maintained pick-lists these forms draw on (Plan D5). */
export interface ReferenceOptions {
  gender: SelectOption[];
  areasOfInterest: SelectOption[];
  /** Where a student studies. Only rendered for VOLUNTEER_TYPE.STUDENT. */
  institutions: SelectOption[];
  /** How they heard about Parinaam. Asked of everybody, and optional. */
  referralSources: SelectOption[];
}

export function toReferenceOptions(
  grouped: Record<string, ReferenceValue[]> | undefined
): ReferenceOptions {
  const pick = (category: string): SelectOption[] =>
    (grouped?.[category] ?? []).map((value) => ({ value: value.code, label: value.label }));

  return {
    gender: pick(REFERENCE_CATEGORY.GENDER),
    areasOfInterest: pick(REFERENCE_CATEGORY.AREA_OF_INTEREST),
    institutions: pick(REFERENCE_CATEGORY.INSTITUTION),
    referralSources: pick(REFERENCE_CATEGORY.REFERRAL_SOURCE),
  };
}
